import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute } from '@tanstack/react-router'
import { type JSX, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'
import { useCurrentOrganization } from '~/hooks'
import { OrganizationMutators } from '~/mutators/organizations'
import { SettingsContent, useSettingTitle } from '~/templates/settings'

import {
  Form,
  FormErrorAlert,
  FormField,
  FormFooterControl,
  Heading2,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  processFormError,
} from '@hap/components'

import { VocabularyList } from './-components/vocabulary/list'

export const Route = createFileRoute('/_authenticated/$alias/settings/transcription/')({
  component: TranscriptionSettings,
})

function TranscriptionSettings(): JSX.Element {
  useSettingTitle(['Transcription'])

  return (
    <SettingsContent spacing="sm">
      <VocabularyList />
      <AudioPreferencesSection />
    </SettingsContent>
  )
}

const preferencesFormSchema = z.object({
  recordingType: z.enum(['auto-detect', 'stereo', 'mono']),
  channelIdentification: z.array(z.enum(['Agent', 'Customer', 'System'])),
  autoAssignSpeakers: z.boolean(),
})
type PreferencesFormValues = z.infer<typeof preferencesFormSchema>

function AudioPreferencesSection(): JSX.Element {
  const organization = useCurrentOrganization()
  const { update } = OrganizationMutators.useUpdate()

  const defaultValues = useMemo((): PreferencesFormValues => {
    const transcription = organization.orgSettings?.transcription
    return {
      recordingType: transcription?.recordingType ?? 'auto-detect',
      channelIdentification: transcription?.channelIdentification ?? [
        'Customer',
        'Agent',
        'System',
      ],
      autoAssignSpeakers: transcription?.autoAssignSpeakers ?? false,
    }
  }, [organization])

  const form = useForm<PreferencesFormValues>({
    resolver: zodResolver(preferencesFormSchema),
    values: defaultValues,
  })

  const recordingType = form.watch('recordingType')

  const handleSubmit = form.handleSubmit(async (values: PreferencesFormValues) => {
    try {
      await update({
        settings: {
          transcription: {
            recordingType: values.recordingType,
            channelIdentification: values.channelIdentification,
            autoAssignSpeakers: values.autoAssignSpeakers,
          },
        },
      })
    } catch (e) {
      processFormError(form.setError, e, {
        genericMessage: 'Failed to update transcription settings',
      })
    }
  })

  const handleReset = () => {
    form.reset(defaultValues)
  }

  return (
    <section className="mt-12 space-y-4">
      <hgroup>
        <Heading2>Audio Settings</Heading2>
        <p className="text-accent-foreground">
          Configure how audio recordings are processed for transcription.
        </p>
      </hgroup>

      <Form {...form}>
        <FormErrorAlert />

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="bg-accent rounded-lg">
            <div className="flex items-center gap-10 p-4">
              <div className="shrink grow">
                <h3 className="text-lg font-semibold">Recording Type</h3>
                <p className="text-accent-foreground">
                  Happitu automatically detects whether recordings are mono or stereo.
                  Override this if your organization consistently uses a specific format.
                </p>
              </div>

              <FormField
                control={form.control}
                name="recordingType"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-78 shrink-0 truncate">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto-detect">
                        Auto-detect (Recommended)
                      </SelectItem>
                      <SelectItem value="stereo">Stereo</SelectItem>
                      <SelectItem value="mono">Mono</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {recordingType !== 'mono' && (
              <>
                <div className="border-t p-4">
                  <FormField
                    control={form.control}
                    name="autoAssignSpeakers"
                    render={({ field }) => (
                      <div className="flex items-center justify-between gap-10">
                        <hgroup>
                          <h3 className="font-semibold">Auto-assign speakers</h3>
                          <p className="text-accent-foreground">
                            Use AI to identify the Agent and Customer in stereo recordings
                            when channel assignments may be unreliable.
                          </p>
                        </hgroup>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="shrink-0"
                        />
                      </div>
                    )}
                  />
                </div>

                <div className="border-t p-4">
                  <div className="space-y-3">
                    <hgroup>
                      <h3 className="font-semibold">Stereo Channel Mapping</h3>
                      <p className="text-accent-foreground">
                        {recordingType === 'auto-detect'
                          ? 'When stereo recordings are detected, this mapping determines which audio channel belongs to which speaker.'
                          : 'Map each audio channel to the corresponding speaker.'}
                      </p>
                    </hgroup>
                    <FormField
                      control={form.control}
                      name="channelIdentification"
                      render={({ field }) => (
                        <>
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Left Audio Channel</Label>
                            <Select
                              value={field.value[0]}
                              onValueChange={(value) => {
                                const newValue = [...field.value]
                                newValue[0] = value as 'Agent' | 'Customer' | 'System'
                                newValue[1] = value === 'Agent' ? 'Customer' : 'Agent'
                                field.onChange(newValue)
                              }}
                            >
                              <SelectTrigger className="w-40">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Agent">Agent</SelectItem>
                                <SelectItem value="Customer">Customer</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Right Audio Channel</Label>
                            <Select
                              value={field.value[1]}
                              onValueChange={(value) => {
                                const newValue = [...field.value]
                                newValue[1] = value as 'Agent' | 'Customer' | 'System'
                                newValue[0] = value === 'Agent' ? 'Customer' : 'Agent'
                                field.onChange(newValue)
                              }}
                            >
                              <SelectTrigger className="w-40">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Agent">Agent</SelectItem>
                                <SelectItem value="Customer">Customer</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <FormFooterControl onReset={handleReset} submitLabel="Save changes" />
        </form>
      </Form>
    </section>
  )
}
